/*
 * Copyright Higharc Inc. 2019
 * Partially ported to JavaScript/GLSL from https://github.com/google/spherical-harmonics which was licensed under Apache License Version 2.0
 * We use the same SH constants as Google's SH library.
 */
import type { CubeTexture, WebGLRenderTarget } from "three";
import { Mesh, MeshBasicMaterial, ShaderMaterial, SphereGeometry, UniformsUtils, Vector3 } from "three";
import { frostedGlassParsFragment } from "../../materials/shaders/frostedGlassTransmission";
import { SH } from "./SH";
import { LightProbeVisualizationMode } from "./SHLightProbeConstants";

const visualizerGeometry = new SphereGeometry(0.15, 16, 16);

const getSHLightProbeShader = function (shOrder: number) {
  const shCoeffCount = SH.getCoefficientVectorCount(shOrder);
  return {
    defines: {},
    uniforms: {
      uShCoeffs: { type: "v3v", value: SH.initSHCoefficients(shOrder, false) },
    },
    vertexShader: `varying vec3 vPosition;

			void main() {
				gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				// This shader relies on that the vertices of the sphere are centered on the model origin.
				vPosition = position;
			}`,
    fragmentShader:
      `#include <common>
			#include <spherical_harmonics_helpers>

			uniform vec4 uShCoeffs[` +
      shCoeffCount +
      `];

			varying vec3 vPosition;

			void main() {
				gl_FragColor = vec4( evalRGBASH( uShCoeffs, normalize(vPosition) ).rgb, 1.0 );
				#include <colorspace_fragment>
			}`,
  };
};

const cubemapLightProbeShader = {
  defines: {},
  uniforms: {
    uCubeMap: { type: "t", value: null },
  },
  vertexShader: `varying vec3 vPosition;

		void main() {
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
			// This shader relies on that the vertices of the sphere are centered on the model origin.
			vPosition = position;
		}`,
  fragmentShader: `
		uniform samplerCube uCubeMap;

		varying vec3 vPosition;

		void main() {
			gl_FragColor = vec4( textureCube( uCubeMap, normalize(vPosition) ).rgb, 1.0 );
			#include <colorspace_fragment>
		}`,
};

/**
 * This visualizes the least blurry mip level of the PMREM.
 */
const pmremLightProbeShader = {
  defines: {},
  uniforms: {
    shLightProbeVizPmrem: { type: "t", value: null },
    shLightProbeVizPmremTexelWidth: { value: 0.0 },
    shLightProbeVizPmremTexelHeight: { value: 0.0 },
    shLightProbeVizPmremMaxMip: { value: 0.0 },
  },
  vertexShader: `varying vec3 vPosition;

		void main() {
			gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
			vPosition = position;
		}`,
  fragmentShader:
    frostedGlassParsFragment.replace("frostedGlass", "shLightProbeViz") +
    `
		varying vec3 vPosition;

		void main() {
			gl_FragColor = fgTextureCubeUV( shLightProbeVizPmrem, normalize(vPosition), 0.0 );
			#include <colorspace_fragment>
		}`,
};

/**
 * Avoid importing the concrete light probe class (breaks a circular dependency).
 */
export interface SHLightProbeVisualizerData {
  skipRender: boolean;
  shOrder?: number;
  colorSHCoefficients?: Vector3[];
  irradianceCubeMapTexture: CubeTexture;
  radianceCubeMapTexture: CubeTexture;
  pmrem: WebGLRenderTarget | null;
}

export class SHLightProbeVisualizer {
  lightProbe: SHLightProbeVisualizerData;
  mesh: Mesh;
  private modeInternal: LightProbeVisualizationMode = LightProbeVisualizationMode.None;
  private shMaterial!: ShaderMaterial;
  private cubeMaterial: ShaderMaterial;
  private pmremMaterial: ShaderMaterial;

  constructor(lightProbe: SHLightProbeVisualizerData) {
    this.lightProbe = lightProbe;
    this.mesh = new Mesh(visualizerGeometry, new MeshBasicMaterial());
    this.cubeMaterial = new ShaderMaterial({
      defines: Object.assign({}, cubemapLightProbeShader.defines),
      fragmentShader: cubemapLightProbeShader.fragmentShader,
      vertexShader: cubemapLightProbeShader.vertexShader,
      uniforms: UniformsUtils.clone(cubemapLightProbeShader.uniforms),
      transparent: true,
      depthTest: false,
    });

    this.pmremMaterial = new ShaderMaterial({
      defines: Object.assign({}, pmremLightProbeShader.defines),
      fragmentShader: pmremLightProbeShader.fragmentShader,
      vertexShader: pmremLightProbeShader.vertexShader,
      uniforms: UniformsUtils.clone(pmremLightProbeShader.uniforms),
      transparent: true,
      depthTest: false,
    });
    this.updateShShader();
    this.mode = LightProbeVisualizationMode.None;
  }

  get mode(): LightProbeVisualizationMode {
    return this.modeInternal;
  }

  set mode(newValue: LightProbeVisualizationMode) {
    this.modeInternal = newValue;
    this.updateData();
  }

  private _shOrder(): number {
    return this.lightProbe.shOrder !== undefined ? this.lightProbe.shOrder : 0;
  }

  updateShShader(): void {
    const shShader = getSHLightProbeShader(this._shOrder());

    this.shMaterial = new ShaderMaterial({
      defines: Object.assign({}, shShader.defines),
      fragmentShader: shShader.fragmentShader,
      vertexShader: shShader.vertexShader,
      uniforms: UniformsUtils.clone(shShader.uniforms),
      transparent: true,
      depthTest: false,
    });
  }

  updateData(): void {
    this.mesh.visible = this.mode !== LightProbeVisualizationMode.None;

    if (!this.mesh.visible) {
      return;
    }

    const vectorCount = SH.getCoefficientVectorCount(this._shOrder());

    if (this.mode === LightProbeVisualizationMode.SkipRender) {
      const skipRenderAsRGB: Vector3[] = [];
      for (let i = 0; i < vectorCount; ++i) {
        skipRenderAsRGB[i] = new Vector3(0.0, 0.0, 0.0);
      }
      if (this.lightProbe.skipRender) {
        skipRenderAsRGB[0] = new Vector3(1.0, 0.0, 1.0);
      } else {
        skipRenderAsRGB[0] = new Vector3(0.0, 0.0, 0.0);
      }
      this.shMaterial.uniforms.uShCoeffs.value = skipRenderAsRGB;
      this.mesh.material = this.shMaterial;
    } else if (this.mode === LightProbeVisualizationMode.IrradianceSH) {
      if (this.lightProbe.shOrder === undefined || this.lightProbe.colorSHCoefficients === undefined) {
        this.shMaterial.uniforms.uShCoeffs.value = [new Vector3(0.0, 0.0, 0.0)];
      } else {
        this.shMaterial.uniforms.uShCoeffs.value = this.lightProbe.colorSHCoefficients;
      }
      this.mesh.material = this.shMaterial;
    } else if (this.mode === LightProbeVisualizationMode.Irradiance) {
      this.cubeMaterial.uniforms.uCubeMap.value = this.lightProbe.irradianceCubeMapTexture;
      this.mesh.material = this.cubeMaterial;
    } else if (this.mode === LightProbeVisualizationMode.Radiance) {
      this.cubeMaterial.uniforms.uCubeMap.value = this.lightProbe.radianceCubeMapTexture;
      this.mesh.material = this.cubeMaterial;
    } else if (this.mode === LightProbeVisualizationMode.PMREM) {
      const pmremTarget = this.lightProbe.pmrem;
      if (pmremTarget) {
        const uniforms = this.pmremMaterial.uniforms;
        uniforms.shLightProbeVizPmrem.value = pmremTarget.texture;
        uniforms.shLightProbeVizPmremTexelWidth.value = 1.0 / pmremTarget.width;
        uniforms.shLightProbeVizPmremTexelHeight.value = 1.0 / pmremTarget.height;
        const cubeSize = pmremTarget.height / 4;
        uniforms.shLightProbeVizPmremMaxMip.value = Math.floor(Math.log2(cubeSize));
      }
      this.mesh.material = this.pmremMaterial;
      this.pmremMaterial.needsUpdate = true;
    }
  }

  dispose(): void {
    this.shMaterial.dispose();
    this.cubeMaterial.dispose();
    this.pmremMaterial.dispose();
  }
}
